import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConstants } from '../../commons/app-constants';
import { DataAssistantAskResponse } from './ai-data-assistant.service';

export interface ChatRequest {
  sessionId?: number | null;
  message: string;
  attachmentBase64?: string | null;
  attachmentName?: string | null;
  attachmentType?: string | null;
  /** 'web' | 'android' | 'ios' — Capacitor.getPlatform(), lets Linc tailor answers to the device. */
  platform?: string | null;
  appVersion?: string | null;
}

export interface ChatResponse {
  sessionId: number;
  messageId: number;
  message: string;
  sources: string[];
  // Added 2026-08-22 -- the backend record has carried these since Phase 4 of this session's
  // earlier work, but this interface never declared them, so TypeScript never surfaced them and
  // nothing downstream ever read them. null = a free reply (greeting/acknowledgment), not an
  // error -- same convention Linc Data's creditsUsed already uses.
  creditsUsedThisCall?: number | null;
  creditsUsedTotal?: number | null;
  creditsTotal?: number | null;
  relatedQuestions?: string[];
  /** The screen this answer points to (a route such as '/consignments/list') — only set by the
   *  backend when the answer comes from a note with a link AND this person may open that screen.
   *  Older backends never send it; treat as absent. */
  deepLink?: string | null;
}

export interface ChatSession {
  id: number;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: number;
  sessionId: number;
  role: 'user' | 'assistant';
  content: string;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentType?: string;
  createdAt: string;
  // Added 2026-08-22 -- null (not shown) for a free reply (greeting/acknowledgment); a real
  // number for a message that actually called OpenAI. Same convention as Linc Data's own
  // creditsUsed on DataChatMessage.
  creditsUsed?: number | null;
  relatedQuestions?: string[];
  deepLink?: string | null;
}

export interface AiChatStatus {
  enabled: boolean;
  blocked: boolean;
  blockedMessage: string | null;
  blockedRemainingSeconds: number;
  dailyCount: number;
  dailyLimit: number;
}

/** One question to the single Auto box (POST /api/v1/ai/auto) — the backend picks Help or Data. */
export interface AutoRequest {
  message: string;
  /** Linc Help conversation id from an earlier Help answer. */
  helpSessionId?: number | null;
  /** Linc Data conversation id from an earlier Data answer. */
  dataSessionId?: string | null;
  /** Which engine answered the previous turn — lets a short follow-up ("and last month?") stay with it. */
  lastEngine?: 'help' | 'data' | null;
  /** Force one engine, e.g. when the person taps an answer to a question that engine just asked. */
  forceEngine?: 'help' | 'data' | null;
  attachmentBase64?: string | null;
  attachmentName?: string | null;
  attachmentType?: string | null;
  platform?: string | null;
  appVersion?: string | null;
}

/** engine 'help' -> help is set; 'data' -> data is set; 'mixed' -> both (how-to first, then data).
 *  note 'DATA_PART_FAILED' = only the how-to half of a mixed question could be answered. */
export interface AutoResponse {
  engine: 'help' | 'data' | 'mixed';
  help: ChatResponse | null;
  data: DataAssistantAskResponse | null;
  note: string | null;
}

/** What Linc will, and will not, give one person (admin preview). Plain words only. */
export interface LincRightsPreview {
  userId: number;
  rbacEnabled: boolean;
  roleName: string | null;
  modulesAssumedOn: boolean;
  dataAssistantAvailable: boolean;
  dataAssistantWhy: string | null;
  /** The same reason as a code (DATA_PLAN / DATA_RIGHT) that the screen shows in the viewer's language. */
  dataAssistantWhyCode?: string | null;
  counts: {
    areasAllowed: number; areasTotal: number;
    questionTypesAllowed: number; questionTypesTotal: number;
    sectionsAllowed: number; sectionsTotal: number;
    approvedAnswersAllowed: number; approvedAnswersTotal: number;
  };
  areas: { label: string; allowed: boolean; why: string | null; whyCode?: string | null }[];
  questionTypes: { name: string; area: string; allowed: boolean; why: string | null; whyCode?: string | null }[];
  notes: { file: string; sections: number; allowed: number; withheld: { title: string; why: string; whyCode?: string | null }[] }[];
}

export interface FeedbackRequest {
  rating: 1 | -1;
  comment?: string;
}

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private base = `${AppConstants.API_URL}api/v1/ai`;
  private appBase = `${AppConstants.API_URL}api/v1/app`;

  constructor(private http: HttpClient) {}

  checkStatus(): Observable<AiChatStatus> {
    return this.http.get<AiChatStatus>(`${this.appBase}/ai-chat-status`);
  }

  chat(req: ChatRequest): Observable<ChatResponse> {
    return this.http.post<ChatResponse>(`${this.base}/chat`, req);
  }

  /** Admin preview: what Linc will give this person. Read-only; the server makes no AI call. */
  lincPreview(userId: number): Observable<LincRightsPreview> {
    return this.http.get<LincRightsPreview>(`${AppConstants.API_URL}api/v1/users/${userId}/linc-preview`);
  }

  /** The single Auto box — one question, the backend picks Help or Data (or both). */
  auto(req: AutoRequest): Observable<AutoResponse> {
    return this.http.post<AutoResponse>(`${this.base}/auto`, req);
  }

  /** Whether to offer the Auto box to this person (server switch on AND Linc Data usable). */
  autoStatus(): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(`${this.base}/auto/status`);
  }

  getSessions(): Observable<ChatSession[]> {
    return this.http.get<ChatSession[]>(`${this.base}/chat/sessions`);
  }

  getMessages(sessionId: number): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(`${this.base}/chat/sessions/${sessionId}/messages`);
  }

  deleteSession(sessionId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/chat/sessions/${sessionId}`);
  }

  emailTranscript(sessionId: number, email?: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.base}/chat/sessions/${sessionId}/email-transcript`,
      email ? { email } : {}
    );
  }

  /** `reason` — added 2026-08-23, only meaningful alongside rating -1 (a thumbs-down "why" chip
   *  pick, see ai-chat-widget.component.ts selectFeedbackReason()) — omit entirely for the
   *  initial rating submit itself; the backend leaves any already-stored reason untouched when
   *  this key is absent, see AiChatController.submitFeedback's own note. */
  submitFeedback(messageId: number, rating: 1 | -1, comment?: string, reason?: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.base}/chat/messages/${messageId}/feedback`,
      { rating, comment, reason }
    );
  }

  // ── Combined AI disclaimer (Linc + "My Data", one acceptance covers both) ──────────────────

  checkDisclaimerStatus(): Observable<{ accepted: boolean }> {
    return this.http.get<{ accepted: boolean }>(`${this.base}/disclaimer-status`);
  }

  acceptDisclaimer(): Observable<void> {
    return this.http.post<void>(`${this.base}/disclaimer-accept`, {});
  }
}
